/**
 * Executive dashboard — the Dealer Principal / GM home (modern design).
 *
 * Every figure is real: the role snapshot (counts, funnel, exceptions,
 * AI-vs-human), the daily series and appointment diary (trends and the
 * "vs previous period" changes), the CRM lead list (sources, hot leads,
 * team, most-enquired models) and CRM customers (at-risk count).
 * Where a number can't be computed it shows "—", never a made-up value.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCrm, stageLabel, stageColor, isClosedStage } from '../../lib/crm';
import { useToast } from '../../lib/toast';
import { downloadCsv, toCsv } from '../../lib/download';
import { useCurrentUser } from '../../lib/currentUser';
import { useOrgName } from '../../lib/useOrgName';
import { useVehicleMap } from '../../lib/useVehicleMap';
import { useDirectoryNames } from '../../lib/useDirectoryNames';
import { sourceLabel } from '../../lib/leadSource';
import { dealValue, isOpenDeal } from '../../lib/deals';
import { periodDays } from '../../components/dashboard/useRoleSnapshot';
import {
  Avatar, DonutLegend, Hero, MiniMetric, Panel, RankList, StatCard, TrendChart, Delta,
} from '../../components/dashboard/modern/ui';
import { Icon } from '../../components/dashboard/modern/icons';
import { fmtAud, fmtInt, pct } from '../../components/dashboard/modern/format';
import {
  carRender, dayLabels, greeting, pipelineFunnel, showroomSlides, useDashboardSnapshot, useDiaryWindow, useExceptionCount, useSeriesPair, withinDays,
} from '../../components/dashboard/modern/useModernData';
import FunnelViz from '../../components/dashboard/modern/FunnelViz';
import QuickActions from '../../components/dashboard/modern/QuickActions';
import { ModuleCard } from '../../components/dashboard/modern/ModuleTiles';

const SOURCE_COLORS = ['#6366f1', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#a855f7', '#94a3b8'];
const SOLD_STAGES = new Set(['RETAILED', 'DELIVERED']);

export default function ExecutiveDashboard({ snapshot, period, periodLabel, contentRef }) {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const orgName = useOrgName();
  const { leads: crmLeads = [], customers = [] } = useCrm();
  const { vehiclesById } = useVehicleMap({ size: 200 });
  const names = useDirectoryNames();
  const toast = useToast();

  const days = periodDays(period);
  const chartDays = Math.max(days, 14);
  const s = snapshot || {};
  const t = s.today || {};
  const w = s.window || {};
  const ex = s.exceptions;

  const leadSeries = useSeriesPair('leads', days, chartDays);
  const bookings = useSeriesPair('bookings', days, chartDays);
  const diary = useDiaryWindow({ days, chartDays });
  const modSnap = useDashboardSnapshot(days);

  // Real customers only — Agent Test Console rows stay out of every figure.
  const leads = useMemo(() => crmLeads.filter((l) => !l.simulated), [crmLeads]);
  const vehicles = useMemo(() => [...(vehiclesById?.values?.() || [])], [vehiclesById]);
  const periodLeads = useMemo(() => leads.filter((l) => withinDays(l.createdAt, days)), [leads, days]);

  /* ---- at-risk customers (no contact in 60+ days) + leads sitting in Booked ---- */
  // Same set /customers?risk=HIGH lists, so the tile and the page agree.
  const atRisk = useMemo(() => customers.filter((c) => c.churnRisk === 'HIGH').length, [customers]);
  const bookedNow = useMemo(() => leads.filter((l) => l.stage === 'DELIVERY_BOOKED').length, [leads]);

  /* ---- module tiles: Lead Accelerator, Customers, Deals (from the CRM) ---- */
  const mods = useMemo(() => {
    // Stalled = open with no activity for 3+ days (the old dashboard's rule).
    let active = 0; let hot = 0; let stalled = 0;
    let openValue = 0; let wonValue = 0; let est = false;
    const byStage = { NEGOTIATION: 0, CONFIRMED: 0, DELIVERY_BOOKED: 0, RETAILED: 0, DELIVERED: 0 };
    leads.forEach((l) => {
      if (!isClosedStage(l.stage)) {
        active += 1;
        if ((l.score ?? 0) >= 80) hot += 1;
        const last = l.lastActivityAt || l.createdAt;
        if (last && !withinDays(last, 3)) stalled += 1;
      }
      if (l.stage in byStage) {
        byStage[l.stage] += 1;
        const { value, estimated } = dealValue(l, l.vehicleOfInterestId ? vehiclesById.get(l.vehicleOfInterestId) : null);
        if (isOpenDeal(l.stage)) { openValue += value; est ||= estimated; }
        if (l.stage === 'DELIVERED') { wonValue += value; est ||= estimated; }
      }
    });
    const realCustomers = customers.filter((c) => !c.simulated);
    const risk = (r) => realCustomers.filter((c) => c.churnRisk === r).length;
    return {
      active, hot, stalled, openValue, wonValue, est, byStage,
      dealCount: Object.values(byStage).reduce((x, y) => x + y, 0),
      customers: realCustomers.length, riskLow: risk('LOW'), riskMed: risk('MED'), riskHigh: risk('HIGH'),
    };
  }, [leads, customers, vehiclesById]);
  // Revival targets by state; "waiting" = in the pool, not yet contacted.
  const revival = useMemo(() => {
    const by = modSnap?.campaignTargetsByState || {};
    const total = modSnap?.totalCampaignTargets ?? 0;
    const contacted = by.CONTACTED || 0; const replied = by.REPLIED || 0; const converted = by.CONVERTED || 0;
    return { total, contacted, replied, converted, waiting: Math.max(0, total - contacted - replied - converted) };
  }, [modSnap]);
  const qa = {
    calls: modSnap?.voiceCalls ?? 0,
    flagged: Math.min(modSnap?.flaggedCalls ?? 0, modSnap?.voiceCalls ?? 0),
    sentiment: Number(modSnap?.avgVoiceSentiment ?? 0),
  };

  /* ---- most-enquired models (period), with a real stock photo ---- */
  // Quiet period: rank the open pipeline instead of showing an empty panel.
  const modelBase = useMemo(
    () => (periodLeads.length ? periodLeads : leads.filter((l) => !isClosedStage(l.stage))),
    [periodLeads, leads],
  );
  const topModels = useMemo(() => {
    const counts = new Map();
    modelBase.forEach((l) => {
      const model = (l.vehicleModel || (l.vehicleOfInterestId && vehiclesById.get(l.vehicleOfInterestId)?.model) || '').trim();
      if (!model) return;
      const key = model.toUpperCase().split(/\s+/).slice(0, 2).join(' ');
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 5).map(([key, value]) => {
      const first = key.split(' ')[0];
      const car = vehicles.find((v) => String(v.model || '').toUpperCase().startsWith(first));
      const make = car?.make || leads.find((l) => (l.vehicleModel || '').toUpperCase().startsWith(first))?.vehicleMake || '';
      return { key, label: `${make ? `${make} ` : ''}${titleCase(key)}`, value, photo: carRender(make, car?.model || key, car?.primaryPhotoUrl) };
    });
  }, [modelBase, vehicles, vehiclesById, leads]);

  /* ---- hero car: most-enquired model, else the priciest in-stock car ---- */
  const heroSlides = useMemo(
    () => showroomSlides(topModels, vehicles, { badge: (i) => `#${i + 1} most enquired` }),
    [topModels, vehicles],
  );

  /* ---- leads by source (period) ---- */
  // Quiet period: show where ALL leads came from rather than an empty donut.
  const sourceBase = periodLeads.length ? periodLeads : leads;
  const sources = useMemo(() => {
    const counts = new Map();
    sourceBase.forEach((l) => counts.set(l.source || 'OTHER', (counts.get(l.source || 'OTHER') || 0) + 1));
    const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    const top = sorted.slice(0, 6);
    const rest = sorted.slice(6).reduce((a, [, v]) => a + v, 0);
    const segs = top.map(([src, value], i) => ({ label: sourceLabel(src), value, color: SOURCE_COLORS[i] }));
    if (rest) segs.push({ label: 'Other', value: rest, color: SOURCE_COLORS[7] });
    return segs;
  }, [sourceBase]);

  /* ---- hot leads (open, score ≥ 80, freshest first) ---- */
  const hotLeads = useMemo(() => leads
    .filter((l) => !isClosedStage(l.stage) && (l.score ?? 0) >= 80)
    .sort((a, b) => (b.score - a.score) || (new Date(b.lastActivityAt || 0) - new Date(a.lastActivityAt || 0)))
    .slice(0, 6), [leads]);

  /* ---- team performance (period) ---- */
  const team = useMemo(() => {
    const by = new Map();
    const row = (id) => { if (!by.has(id)) by.set(id, { id, leads: 0, sold: 0, open: 0 }); return by.get(id); };
    leads.forEach((l) => {
      if (!l.assignedConsultantId) return;
      const r = row(l.assignedConsultantId);
      if (withinDays(l.createdAt, days)) r.leads += 1;
      if (SOLD_STAGES.has(l.stage) && withinDays(l.stageEnteredAt, days)) r.sold += 1;
      if (!isClosedStage(l.stage)) r.open += 1;
    });
    return [...by.values()]
      .map((r) => ({ ...r, name: names.consultant(r.id, null, { dash: null }) || 'Consultant' }))
      .filter((r) => r.leads || r.sold || r.open)
      .sort((a, b) => (b.sold - a.sold) || (b.leads - a.leads) || (b.open - a.open))
      .slice(0, 5);
  }, [leads, days, names]);

  /* ---- funnel ---- */
  // Current pipeline — every real lead at the furthest stage it reached.
  const funnel = useMemo(() => pipelineFunnel(leads), [leads]);

  const ai = s.aiVsHuman;
  const aiTotal = ai ? (ai.aiOwned || 0) + (ai.humanOwned || 0) + (ai.shared || 0) : 0;
  // Distinct leads, matching the Exceptions list the chip opens (all CRM
  // leads, as the board shows them).
  const exceptionTotal = useExceptionCount(crmLeads);

  // Same export the old dashboard's Quick actions ran: every lead, one row each.
  const exportLeadsCsv = () => {
    if (!crmLeads.length) { toast('No leads to export', { variant: 'info' }); return; }
    const nameById = new Map(customers.map((c) => [String(c.id), c.name]));
    const rows = crmLeads.map((l) => [
      nameById.get(String(l.customerId)) || l.customerName || l.customerId || '',
      stageLabel(l.stage) || l.stage || '',
      l.score ?? '',
      l.source || '',
      l.createdAt ? new Date(l.createdAt).toISOString().slice(0, 10) : '',
    ]);
    downloadCsv(`leads-${new Date().toISOString().slice(0, 10)}.csv`,
      toCsv(['Customer', 'Stage', 'Score', 'Source', 'Created'], rows));
    toast(`Exported ${crmLeads.length} leads to CSV`, { variant: 'success' });
  };
  const quickActions = [
    { key: 'c', label: 'New customer', sub: 'Add a contact', icon: Icon.userPlus, tone: '#6366f1', to: '/customers?new=1' },
    { key: 'd', label: 'New deal', sub: 'Build a quote', icon: Icon.dealPlus, tone: '#10b981', to: '/quotes/new' },
    { key: 'l', label: 'New lead', sub: 'Log an enquiry', icon: Icon.leadPlus, tone: '#3b82f6', to: '/leads?new=1' },
    { key: 'b', label: 'Send broadcast', sub: 'SMS / email campaign', icon: Icon.megaphone, tone: '#f59e0b', to: '/campaigns' },
    { key: 'a', label: 'Audit pack', sub: 'Compliance records', icon: Icon.shield, tone: '#8b5cf6', to: '/compliance' },
    { key: 'x', label: 'Export CSV', sub: 'All leads', icon: Icon.download, tone: '#06b6d4', onClick: exportLeadsCsv },
  ];

  const firstName = user?.firstName || (user?.displayName || user?.name || '').split(' ')[0] || '';
  // The snapshot's "appointments" includes the test drives and deliveries;
  // show the other appointments so the three chips don't double-count.
  const otherAppts = Math.max(0, (t.appointments ?? 0) - (t.testDrives ?? 0) - (t.deliveries ?? 0));
  const chips = [
    { key: 'a', value: fmtInt(otherAppts), label: 'appointments today', to: '/appointments' },
    { key: 't', value: fmtInt(t.testDrives ?? 0), label: 'test drives today', to: '/test-drives' },
    { key: 'd', value: fmtInt(t.deliveries ?? 0), label: 'deliveries today', to: '/appointments' },
  ];
  if (exceptionTotal > 0) chips.push({ key: 'x', alert: true, value: fmtInt(exceptionTotal), label: 'need a decision', to: '/leads?filter=exceptions' });

  return (
    <div className="app-content md-page" ref={contentRef}>
      <Hero
        eyebrow={`Executive dashboard${orgName ? ` · ${orgName}` : ''}`}
        title={`${greeting()}${firstName ? `, ${firstName}` : ''}.`}
        highlight="Here's your dealership."
        sub={`Real conversations, real customers, real results — ${periodLabel === 'Today' ? 'today' : `the last ${periodLabel}`} at a glance.`}
        chips={chips}
        slides={heroSlides}
      />

      {/* ── KPI strip ── */}
      <div className="md-stats">
        <StatCard index={0} label="New leads" icon={Icon.users} tone="#6366f1" to="/leads"
          value={fmtInt(w.newLeads?.total ?? leadSeries.total)} delta={leadSeries.delta}
          spark={leadSeries.values} sub={`${periodLabel} · vs previous`} />
        <StatCard index={1} label="Appointments" icon={Icon.calendar} tone="#10b981" to="/appointments"
          value={fmtInt(w.appointments?.booked ?? diary.apptTotal)} delta={diary.apptDelta}
          spark={diary.appts} sub={`${fmtInt(w.appointments?.attended)} attended`} />
        <StatCard index={2} label="Test drives" icon={Icon.wheel} tone="#06b6d4" to="/test-drives"
          value={fmtInt(w.testDrives?.total ?? diary.driveTotal)} delta={diary.driveDelta}
          spark={diary.drives} sub="booked in the diary" />
        <StatCard index={3} label="Vehicles sold" icon={Icon.car} tone="#8b5cf6" to="/deals"
          value={fmtInt(w.sold?.total)} sub={`${fmtInt(w.deliveries)} delivered`} />
        <StatCard index={4} label="Bookings" icon={Icon.check} tone="#f59e0b" to={`/leads?filter=booked&days=${days}`}
          value={fmtInt(bookings.total)} delta={bookings.delta} spark={bookings.values}
          sub={`${fmtInt(bookedNow)} in Booked now`} />
        <StatCard index={5} label="At-risk customers" icon={Icon.alert} tone="#ef4444" to="/customers?risk=HIGH"
          value={fmtInt(atRisk)} sub="no contact in 60+ days" />
      </div>

      <QuickActions actions={quickActions} />

      {/* ── Funnel · appointments & test drives ── */}
      <div className="md-row c2f">
        <Panel title="Sales funnel" sub="Every lead by the furthest board stage it reached." icon={Icon.funnel} link="/leads" linkLabel="Board">
          <FunnelViz funnel={funnel} />
        </Panel>

        <Panel title="Appointments & test drives" sub={`Daily, last ${chartDays} days.`} icon={Icon.calendar} tone="#10b981" link="/appointments" linkLabel="Diary">
          <div className="md-trend">
            <div>
              <div className="md-trend-legend" style={{ marginBottom: 6 }}>
                <span><i style={{ background: '#6366f1' }} />Appointments</span>
                <span><i style={{ background: '#10b981', borderRadius: 2 }} />Test drives</span>
              </div>
              <TrendChart height={300} labels={dayLabels(chartDays)} series={[
                { label: 'Appointments', color: '#6366f1', values: diary.appts },
                { label: 'Test drives', color: '#10b981', values: diary.drives, type: 'bar' },
              ]} />
            </div>
            <div className="md-trend-side">
              <div>
                <div className="md-big">{fmtInt(diary.apptTotal)}</div>
                <div className="md-small">appointments · {periodLabel}</div>
                <Delta value={diary.apptDelta} />
              </div>
              <div>
                <div className="md-big">{fmtInt(diary.driveTotal)}</div>
                <div className="md-small">test drives · {periodLabel}</div>
                <Delta value={diary.driveDelta} />
              </div>
            </div>
          </div>
        </Panel>

      </div>

      {/* ── Sources · engagement · most enquired ── */}
      <div className="md-row c3">
        <Panel title="Leads by source" sub={periodLeads.length ? `New leads · ${periodLabel}.` : 'All leads — none new in this period.'} icon={Icon.pie} tone="#3b82f6">
          <DonutLegend segments={sources} centerLabel="leads" />
        </Panel>

        <Panel title="Lead engagement" sub="Faster responses, higher conversion." icon={Icon.bolt} tone="#f59e0b">
          <div className="md-minis">
            <MiniMetric icon={Icon.bot} tone="#8b5cf6" label="of leads handled by AI"
              value={aiTotal ? `${pct(ai.aiOwned + (ai.shared || 0), aiTotal)}%` : '—'} />
            <MiniMetric icon={Icon.alert} tone={ex?.unactionedLeads ? '#ef4444' : '#10b981'} label="new leads not yet actioned"
              value={fmtInt(ex?.unactionedLeads)} to="/leads?filter=unactioned" />
            <MiniMetric icon={Icon.clock} tone={ex?.leadsWithoutNextEvent ? '#f59e0b' : '#10b981'} label="open leads with no next step"
              value={fmtInt(ex?.leadsWithoutNextEvent)} to="/leads?filter=no-next-step" />
          </div>
          <div className="md-minis" style={{ marginTop: 10, flex: 'none' }}>
            <MiniMetric icon={Icon.check} tone="#10b981" label="lead → appointment"
              value={w.conversion?.leadToAppointment != null ? `${Math.round(w.conversion.leadToAppointment * 100)}%` : '—'} />
            <MiniMetric icon={Icon.wheel} tone="#06b6d4" label="appointment → test drive"
              value={w.conversion?.appointmentToTestDrive != null ? `${Math.round(w.conversion.appointmentToTestDrive * 100)}%` : '—'} />
            <MiniMetric icon={Icon.users} tone="#ef4444" label="appointment no-shows"
              value={fmtInt(w.appointments?.noShow)} />
          </div>
        </Panel>
        <Panel title="Most enquired vehicles" sub={periodLeads.length ? `By new leads · ${periodLabel}.` : 'By open leads — none new in this period.'} icon={Icon.car} tone="#8b5cf6" link="/inventory" linkLabel="Inventory">
          <RankList rows={topModels} />
        </Panel>
      </div>

      {/* ── Hot leads · team ── */}
      <div className="md-row c2">
        <Panel title="Recent hot leads" sub="High-intent customers to act on." icon={Icon.flame} tone="#ef4444" link="/leads?filter=hot">
          {hotLeads.length ? (
            <table className="md-table">
              <thead><tr><th>Customer</th><th>Vehicle</th><th>Stage</th><th>Owner</th></tr></thead>
              <tbody>
                {hotLeads.map((l) => (
                  <tr key={l.id} onClick={() => navigate(`/leads/${l.id}`)}>
                    <td><span className="md-person"><Avatar name={l.name} size={26} />{l.name}</span></td>
                    <td>{l.vehicleModel || l.vehicle || '—'}</td>
                    <td><span className="md-pill" style={{ '--md-tone': stageColor(l.stage) }}>{stageLabel(l.stage)}</span></td>
                    <td>{l.assignedConsultantId ? (names.consultant(l.assignedConsultantId, null, { dash: null }) || '—').split(' ')[0] : <span style={{ color: 'var(--warning)' }}>Unassigned</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="md-empty">No hot leads right now.</div>}
        </Panel>

        <Panel title="Team performance" sub={`Leads and sales · ${periodLabel}.`} icon={Icon.trophy} tone="#f59e0b" link="/team" linkLabel="Team">
          {team.length ? (
            <table className="md-table">
              <thead><tr><th>Consultant</th><th style={{ textAlign: 'right' }}>Leads</th><th style={{ textAlign: 'right' }}>Sold</th><th style={{ textAlign: 'right' }}>Open</th></tr></thead>
              <tbody>
                {team.map((r) => (
                  <tr key={r.id} onClick={() => navigate('/team')}>
                    <td><span className="md-person"><Avatar name={r.name} size={26} />{r.name}</span></td>
                    <td style={{ textAlign: 'right' }}>{r.leads}</td>
                    <td style={{ textAlign: 'right', color: 'var(--success)', fontWeight: 700 }}>{r.sold}</td>
                    <td style={{ textAlign: 'right' }}>{r.open}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="md-empty">No consultant activity in this period.</div>}
        </Panel>
      </div>

      {/* ── Modules ── */}
      <section className="md-mods-wrap">
        <div className="md-mods-head"><b>Modules at a glance</b><span>Live status of each workspace — click a card to open it.</span></div>
        <div className="md-mods">
          <ModuleCard index={0} title="Lead Accelerator" icon={Icon.bolt} tone="#6366f1" to="/leads"
            value={fmtInt(mods.active)} label="open leads"
            meta={`${fmtInt(mods.hot)} hot · ${fmtInt(leadSeries.total)} new in ${periodLabel}`}
            ring={{ value: `${pct(mods.active - mods.stalled, mods.active)}%`, label: 'fresh' }}
            segments={[
              { label: 'Fresh', value: mods.active - mods.stalled, color: '#6366f1' },
              { label: 'Stalled 3d+', value: mods.stalled, color: '#ef4444' },
            ]} />
          <ModuleCard index={1} title="Lost leads" icon={Icon.users} tone="#f59e0b" to="/dead-leads"
            value={modSnap ? fmtInt(revival.total) : '—'} label="revival targets"
            meta="Win-back campaign"
            ring={{ value: `${pct(revival.total - revival.waiting, revival.total)}%`, label: 'reached' }}
            segments={[
              { label: 'Waiting', value: revival.waiting, color: '#94a3b8' },
              { label: 'Contacted', value: revival.contacted, color: '#3b82f6' },
              { label: 'Replied', value: revival.replied, color: '#f59e0b' },
              { label: 'Converted', value: revival.converted, color: '#10b981' },
            ]} />
          <ModuleCard index={2} title="Triage & QA" icon={Icon.phone} tone="#06b6d4" to="/triage"
            value={modSnap ? fmtInt(qa.calls) : '—'} label="voice calls"
            meta={modSnap ? `Avg sentiment ${qa.sentiment.toFixed(2)}` : periodLabel}
            ring={{ value: `${pct(qa.calls - qa.flagged, qa.calls)}%`, label: 'clean' }}
            segments={[
              { label: 'Clean', value: qa.calls - qa.flagged, color: '#10b981' },
              { label: 'Flagged', value: qa.flagged, color: '#ef4444' },
            ]} />
          <ModuleCard index={3} title="Customers" icon={Icon.team} tone="#3b82f6" to="/customers"
            value={fmtInt(mods.customers)} label="in CRM"
            meta={`${fmtInt(mods.riskHigh)} at risk · ${fmtInt(mods.hot)} hot`}
            ring={{ value: `${pct(mods.riskLow, mods.customers)}%`, label: 'low risk' }}
            segments={[
              { label: 'Low risk', value: mods.riskLow, color: '#10b981' },
              { label: 'Medium', value: mods.riskMed, color: '#f59e0b' },
              { label: 'High risk', value: mods.riskHigh, color: '#ef4444' },
            ]} />
          <ModuleCard index={4} title="Deals" icon={Icon.coins} tone="#8b5cf6" to="/deals"
            value={`${mods.est ? '~' : ''}${fmtAud(mods.openValue)}`} label="open pipeline"
            meta={`${mods.est ? '~' : ''}${fmtAud(mods.wonValue)} won${mods.est ? ' · ~ estimated' : ''}`}
            ring={{ value: fmtInt(mods.dealCount), label: 'deals' }}
            segments={[
              { label: 'Negotiation', value: mods.byStage.NEGOTIATION, color: '#8b5cf6' },
              { label: 'Quoted', value: mods.byStage.CONFIRMED, color: '#6366f1' },
              { label: 'Booked', value: mods.byStage.DELIVERY_BOOKED, color: '#3b82f6' },
              { label: 'Retailed', value: mods.byStage.RETAILED, color: '#06b6d4' },
              { label: 'Delivered', value: mods.byStage.DELIVERED, color: '#10b981' },
            ]} />
        </div>
      </section>
    </div>
  );
}

function titleCase(s) {
  return String(s).toLowerCase().replace(/(^|[\s-])(\w)/g, (m, a, b) => a + b.toUpperCase()).replace(/\bX-trail\b/i, 'X-Trail');
}
